import { useTracking } from '@vp/react-tracking';
import React, { useState } from 'react';
import { Card, FlexBox, Link, Typography, responsive, useScreenClass } from '@vp/swan';
import { FormattedReactMessage, __ } from '@99designs/i18n';
import { LegacyRequest } from '../hooks/useCombinedProjects';
import DesignServicesModal from './DesignServicesModal';
import { CTA, RequestCardCTA } from './RequestCardCTA';
import { RequestCardImage } from './RequestCardImage';
import { RequestCardSummary } from './RequestCardSummary';

const ResponsiveFlexBox = responsive(FlexBox);
const aspectRatioImg =
    'https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/AspectRatio.png';

interface NonWorkEntityRequestCardProps {
    request: LegacyRequest;
}

export const NonWorkEntityRequestCard: React.FC<NonWorkEntityRequestCardProps> = ({ request }) => {
    const screenClass = useScreenClass();
    const isXs = screenClass === 'xs';
    const isLogoProject = request.type === 'Logo';
    const lastDownload = request.downloads[request.downloads.length - 1];
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { tracking } = useTracking();

    const ctas: CTA[] = isLogoProject
        ? [
              {
                  label: __('Download logo files'),
                  iconType: 'download',
                  href: lastDownload ? lastDownload : '',
                  onClick: () => {
                      tracking.track('Navigation Clicked', {
                          action: 'Navigation Clicked',
                          navigationDetail: 'download-logo-files',
                          eventDetail: 'Download logo files',
                      });
                  },
                  openInNewTab: false,
                  skin: 'primary',
              },
          ]
        : [
              {
                  label: __('Go to My Projects'),
                  iconType: 'myProjects',
                  href: '/my-account/design/projects',
                  onClick: () => {
                      tracking.track('Navigation Clicked', {
                          action: 'Navigation Clicked',
                          navigationDetail: 'open-my-projects',
                      });
                  },
                  openInNewTab: false,
                  skin: 'primary',
              },
          ];

    function openModal() {
        setIsModalOpen(true);
        tracking.track('Navigation Clicked', {
            action: 'Navigation Clicked',
            navigationDetail: 'start-new-project:legacy',
            eventDetail: 'start a new project',
        });
    }

    function openSupport() {
        tracking.track('Navigation Clicked', {
            action: 'Navigation Clicked',
            navigationDetail: 'contact-support:legacy',
            eventDetail: 'contact support',
        });
        window.location.href = '/customer-care/help-centre/';
    }

    const sendAnalyticsForMyProjects = () =>
        tracking.track('Navigation Clicked', {
            action: 'Navigation Clicked',
            navigationDetail: 'go-to-my-projects:legacy',
            eventDetail: 'Go to My Projects',
        });

    return (
        <Card bordered>
            <ResponsiveFlexBox
                xs={{
                    flexDirection: 'column',
                    gap: 'between-subsections',
                    alignItems: 'center',
                }}
                sm={{
                    flexDirection: 'row',
                    gap: 'between-sections',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                }}
            >
                <RequestCardImage
                    imgSrc={isLogoProject ? request.previewRef : aspectRatioImg}
                    imgAlt={__('Design Preview')}
                />
                <ResponsiveFlexBox
                    style={{ flex: '1 1 100%' }}
                    xs={{
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: 'between-subsections',
                    }}
                    sm={{ alignItems: 'flex-start' }}
                >
                    <ResponsiveFlexBox
                        xs={{
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 'between-actions',
                        }}
                        sm={{ alignItems: 'flex-start' }}
                    >
                        <RequestCardSummary
                            title={request.title ?? undefined}
                            createdAt={request.createdAt}
                            requestType={request.type}
                        />
                        <Typography fontSkin="body-standard" textAlign={isXs ? 'center' : 'left'}>
                            {isLogoProject ? (
                                __(
                                    'This service has been retired due to system updates. This means revisions are no longer possible, but you can still download your latest logo files.'
                                )
                            ) : (
                                <FormattedReactMessage
                                    message={__(
                                        'This service has been retired due to system updates. This means revisions are no longer possible, but you can still access your completed designs in <Link>My Projects</Link>.'
                                    )}
                                    replacements={{
                                        Link: (
                                            <Link
                                                href="/my-account/design/projects"
                                                onClick={sendAnalyticsForMyProjects}
                                            />
                                        ),
                                    }}
                                />
                            )}
                        </Typography>
                        <Typography fontSkin="body-standard" textAlign={isXs ? 'center' : 'left'}>
                            <FormattedReactMessage
                                message={__(
                                    'If you need an update to your design, you can <StartProjectLink>start a new project</StartProjectLink> or <ContactSupportLink>contact support</ContactSupportLink> to discuss your design needs'
                                )}
                                replacements={{
                                    StartProjectLink: <Link onClick={openModal} />,
                                    ContactSupportLink: <Link onAbort={openSupport} />,
                                }}
                            />
                        </Typography>
                    </ResponsiveFlexBox>
                </ResponsiveFlexBox>
                <RequestCardCTA ctas={ctas} />
            </ResponsiveFlexBox>
            <DesignServicesModal isModalOpen={isModalOpen} setIsModalOpen={setIsModalOpen} />
        </Card>
    );
};
